import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { QueryLoader, Link } from "@jsq/ds/core";
import {
  DocumentType,
  RequestedDocumentType,
  useGetQuestionnaireQuery,
} from "../../graphql/supergraph.index";
import { ComplianceObjectType } from "../../graphql/supergraphOperations";
import { hasMissingDocuments } from "../documents/DocumentCollectorInvestor/utils";
import { DocumentStatusLabel } from "../documents/DocumentStatusLabel";
import { ProfileActionOverview } from "../ProfileActionOverview";
import { provideLegalName } from "../ProfileTree/utils";
import { ProfileActionOverviewScreenProps } from "./types";

export const ProfileActionOverviewScreen: React.FC<ProfileActionOverviewScreenProps> = ({
  caseResult,
  basePath,
  profileId: profileIdFromProps,
  showBreadcrumb,
  showLinkToIraSelector,
}) => {
  const { profileId: profileIdFromUrl } = useParams<{ profileId: string }>();
  const history = useHistory();
  const profileId = profileIdFromProps || parseInt(profileIdFromUrl);
  const complianceCase = caseResult.data?.complianceCasesByObjectId[0];
  const allProfiles = complianceCase?.profiles.concat([complianceCase?.investingProfile]);
  const profile = allProfiles?.filter((p) => p?.id === profileId)[0];

  // All fields are required, so checking just one is sufficient
  const areDetailsCompleted =
    profile?.complianceObjectType === "individual"
      ? !!profile.dateOfBirth
      : !!profile?.amlEntityTypeId;
  const areDocumentsCompleted = !hasMissingDocuments(
    profile?.rulesEngine?.documentTypes || [],
    profile?.documents || [],
  );
  const areDocumentsStarted = !!profile?.documents.length;
  !!profile?.documents.length;
  const areOwnersCompleted = !!profile?.caseDataEntryStatus?.ownersCompleted;
  const areControllersCompleted = !!profile?.caseDataEntryStatus?.controllersCompleted;

  const caseId = complianceCase?.id;
  const questionsResult = useGetQuestionnaireQuery({
    variables: { caseId: caseId || 0 },
    skip: !caseId,
  });
  const profileName = provideLegalName(complianceCase?.investingProfile);
  // If there is only one (default) question, no reason to show the survey.
  // The aml_entity_type will already be set.
  const showSurvey =
    !!questionsResult.data?.complianceCase?.investingProfile?.amlEntityTypeQuestions.length;
  const isRoot = profileId === complianceCase?.investingProfile?.id;
  const isOwner = !!profile?.parentRelationships?.some(
    (pr) => pr?.relationshipType === "owner",
  );
  const isController = !!profile?.parentRelationships?.some(
    (pr) => pr?.relationshipType === "controller",
  );
  return (
    <QueryLoader result={[questionsResult, caseResult]}>
      <div>
        {showBreadcrumb && (
          <Link
            to={`${basePath}`}
            variant="muted"
            icon="arrow-left"
            iconPosition="left"
            align="left"
          >
            {profileName}
          </Link>
        )}
        {showLinkToIraSelector && (
          <Link
            to={`${basePath}/ira-categorization`}
            data-testid="beneficial-owner-selection-back-link"
            variant="muted"
            icon="arrow-left"
            iconPosition="left"
            align="left"
          >
            Select account type
          </Link>
        )}
        <ProfileActionOverview
          showDetails={(isRoot && showSurvey) || profile?.complianceObjectType === "individual"}
          showDocuments={isRoot}
          profileName={provideLegalName(profile)}
          profileType={profile?.complianceObjectType as ComplianceObjectType}
          detailsCompleted={areDetailsCompleted}
          documentsCompleted={areDocumentsCompleted}
          documentsWarning={
            <DocumentStatusLabel
              documentTypes={profile?.rulesEngine?.documentTypes as RequestedDocumentType[]}
              documents={profile?.documents as DocumentType[]}
            />
          }
          documentsStarted={areDocumentsStarted}
          ownersCompleted={areOwnersCompleted}
          controllersCompleted={areControllersCompleted}
          onDetailsClick={() => {
            if (profile?.complianceObjectType === "individual") {
              history.push(`${basePath}/edit-individual/${profileId}`);
            } else {
              history.push(`${basePath}/edit-institution/${profileId}`);
            }
          }}
          onDocumentsClick={() => {
            history.push(`${basePath}/documents/${profileId}`);
          }}
          onOwnersClick={() => {
            history.push(`${basePath}/owners/threshold-check/${profileId}`);
          }}
          onControllersClick={() => {
            history.push(`${basePath}/controllers/${profileId}`);
          }}
          showOwners={isRoot || isOwner}
          showControllers={isRoot || isController}
        />
      </div>
    </QueryLoader>
  );
};
